import { useFormContext } from 'react-hook-form';
import { useQueryHelper } from '@anytag/shared-hooks';
import { getValuesArray } from '@anytag/shared-atoms';
import { getSelectOptions } from '@anytag/shared-utils';
import { useAllInfluencersV4LazyQuery } from '@anytag/graphql';
import { SelectValueType } from '../selector-field';
import { AsyncSelectorSearchField, AsyncSelectorSearchFieldProps } from '../async-selector-search-field';
import {
  InfluencerSearchOption,
  AsyncSearchInfluencerFieldProps,
  SingleAsyncSearchInfluencerFieldProps,
  MultipleAsyncSearchInfluencerFieldProps
} from './types';

// Function overloads for proper type inference based on `multiple` prop
export function AsyncSearchInfluencerField<T extends string>(
  props: MultipleAsyncSearchInfluencerFieldProps<T>
): React.ReactElement;
export function AsyncSearchInfluencerField<T extends string>(
  props: SingleAsyncSearchInfluencerFieldProps<T>
): React.ReactElement;
export function AsyncSearchInfluencerField<T extends string>({
  name,
  multiple,
  searchProps,
  ...restProps
}: AsyncSearchInfluencerFieldProps<T>): React.ReactElement {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getAllInfluencersV4, loading } = useAllInfluencersV4LazyQuery();
  const { watch } = useFormContext<{ [name: string]: SelectValueType<InfluencerSearchOption> }>();

  const value = watch(name);
  const selectedInfluencers = getValuesArray({ value, multiple }) as string[]; // use string[] internally for comparisons

  const fetchInfluencers: AsyncSelectorSearchFieldProps<T, InfluencerSearchOption>['onSearch'] = async (keyword) => {
    let influencers: InfluencerSearchOption[] = [];

    try {
      const { data } = await getAllInfluencersV4({
        variables: {
          keyword,
          offset: 0,
          limit: 200, // currently set based on approx outcome, we cannot use unlimited search due to high load on the BE side
          orderBy: { field: null, order: null },
          includeIds: selectedInfluencers?.map(Number).filter((id) => !Number.isNaN(id))
        }
      });
      influencers = getSelectOptions(data?.allInfluencersV4?.influencers);
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }

    return influencers;
  };

  const commonProps: Pick<
    AsyncSelectorSearchFieldProps<T, InfluencerSearchOption>,
    'name' | 'loading' | 'onSearch' | 'placeholder' | 'searchProps'
  > = {
    name,
    loading,
    onSearch: fetchInfluencers,
    placeholder: t<string>('General.Influencer'),
    searchProps: { placeholder: t<string>('Type to search and display options'), ...searchProps }
  };

  return multiple ? (
    <AsyncSelectorSearchField<T, InfluencerSearchOption>
      {...(restProps as Partial<MultipleAsyncSearchInfluencerFieldProps<T>>)}
      {...commonProps}
      multiple={true}
    />
  ) : (
    <AsyncSelectorSearchField<T, InfluencerSearchOption>
      {...(restProps as Partial<SingleAsyncSearchInfluencerFieldProps<T>>)}
      {...commonProps}
      multiple={false}
    />
  );
}
